import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  ArrowLeft, Mail, Phone, Calendar, Edit, Save, 
  ShoppingBag, IndianRupee, TrendingUp, Clock, 
  Tags, MessageSquare, ExternalLink, Users,
  AlertCircle, CheckCircle, XCircle, Loader2
} from 'lucide-react';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';

const CustomerDetailsNew = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { apiCall } = useAuth();
  
  const [customerData, setCustomerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '' });

  useEffect(() => {
    fetchCustomerDetails();
  }, [id]);

  const fetchCustomerDetails = async () => {
    try {
      setLoading(true);
      const data = await apiCall(`/api/customers/${id}/details`);
      setCustomerData(data);
      setFormData({ name: data.customer.name, email: data.customer.email });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load customer details',
        variant: 'destructive'
      });
      navigate('/customers');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setFormData({ name: customerData.customer.name, email: customerData.customer.email });
    setEditDialog(true);
  };

  const handleSave = async () => {
    try {
      await apiCall(`/api/customers/${id}?name=${encodeURIComponent(formData.name)}`, {
        method: 'PUT'
      });

      toast({
        title: 'Success',
        description: 'Customer updated successfully'
      });

      setEditDialog(false);
      fetchCustomerDetails();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'completed': 'bg-green-100 text-green-800',
      'delivered': 'bg-green-100 text-green-800',
      'paid': 'bg-green-100 text-green-800',
      'pending': 'bg-yellow-100 text-yellow-800',
      'processing': 'bg-blue-100 text-blue-800',
      'shipped': 'bg-blue-100 text-blue-800',
      'cancelled': 'bg-red-100 text-red-800',
      'refunded': 'bg-red-100 text-red-800',
      'open': 'bg-yellow-100 text-yellow-800',
      'closed': 'bg-gray-100 text-gray-800',
      'resolved': 'bg-green-100 text-green-800'
    };
    return statusColors[status?.toLowerCase()] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64" data-testid="loading-spinner">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
          <span className="ml-2 text-gray-500">Loading customer details...</span>
        </div>
      </DashboardLayout>
    );
  }

  if (!customerData) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-500">Customer not found</p>
        </div>
      </DashboardLayout>
    );
  }

  const { customer, metrics, orders, segments, support_tickets, crm_links } = customerData;

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-7xl" data-testid="customer-details-page">
        {/* Back Button */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/customers')} data-testid="back-button">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Customers
          </Button>
        </div>
        
        {/* Customer Header */}
        <Card data-testid="customer-header-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <Avatar className="h-20 w-20">
                  <AvatarFallback className="bg-gray-900 text-white text-2xl">
                    {customer.name?.split(' ').map(n => n[0]).join('') || '?'}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <h1 className="text-2xl font-bold" data-testid="customer-name">{customer.name}</h1>
                    <Badge variant={customer.status === 'active' ? 'default' : 'secondary'}>
                      {customer.status}
                    </Badge>
                  </div>
                  <div className="space-y-1 text-sm text-gray-600">
                    <p className="flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      {customer.email}
                    </p>
                    {customer.phone && (
                      <p className="flex items-center gap-2">
                        <Phone className="h-4 w-4" />
                        {customer.phone}
                      </p>
                    )}
                    <p className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Joined {formatDate(customer.created_at)}
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleEdit} data-testid="edit-customer-btn">
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Customer
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="metrics-section">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Orders</p>
                  <p className="text-2xl font-bold" data-testid="total-orders">{metrics.total_orders}</p>
                </div>
                <ShoppingBag className="h-8 w-8 text-blue-500 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Spent</p>
                  <p className="text-2xl font-bold" data-testid="total-spent">{formatCurrency(metrics.total_spent)}</p>
                </div>
                <IndianRupee className="h-8 w-8 text-green-500 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Avg Order Value</p>
                  <p className="text-2xl font-bold" data-testid="avg-order-value">{formatCurrency(metrics.avg_order_value)}</p>
                </div>
                <TrendingUp className="h-8 w-8 text-purple-500 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-indigo-50 to-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Customer Lifetime Value</p>
                  <p className="text-2xl font-bold text-indigo-600" data-testid="clv">{formatCurrency(metrics.clv)}</p>
                  <p className="text-xs text-gray-400">{metrics.months_as_customer} months as customer</p>
                </div>
                <TrendingUp className="h-8 w-8 text-indigo-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Orders & Support */}
          <div className="lg:col-span-2 space-y-6">
            {/* Past Orders */}
            <Card data-testid="orders-section">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ShoppingBag className="h-5 w-5" />
                  Past Orders
                </CardTitle>
                <CardDescription>
                  {metrics.first_order_date && `First order: ${formatDate(metrics.first_order_date)}`}
                  {metrics.last_order_date && ` • Last order: ${formatDate(metrics.last_order_date)}`}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {orders.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Order #</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Items</TableHead>
                        <TableHead>Total</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {orders.map((order) => (
                        <TableRow key={order.order_id} className="cursor-pointer hover:bg-gray-50">
                          <TableCell>
                            <Link 
                              to={`/orders/${order.order_id}`} 
                              className="text-blue-600 hover:underline font-medium"
                            >
                              #{order.order_number}
                            </Link>
                          </TableCell>
                          <TableCell>{formatDate(order.order_date)}</TableCell>
                          <TableCell>{order.items_count} item(s)</TableCell>
                          <TableCell className="font-medium">{formatCurrency(order.total)}</TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(order.status)}`}>
                              {order.status}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <ShoppingBag className="h-12 w-12 mx-auto mb-2 opacity-30" />
                    <p>No orders yet</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Support Tickets */}
            <Card data-testid="support-section">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertCircle className="h-5 w-5" />
                  Issues Raised
                </CardTitle>
                <CardDescription>Support tickets from Shopify and internal system</CardDescription>
              </CardHeader>
              <CardContent>
                {support_tickets.length > 0 ? (
                  <div className="space-y-3">
                    {support_tickets.map((ticket) => (
                      <div 
                        key={ticket.ticket_id} 
                        className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
                      >
                        <div className="flex-1">
                          <p className="font-medium">{ticket.subject}</p>
                          <div className="flex items-center gap-2 text-sm text-gray-500">
                            <span>{formatDate(ticket.created_at)}</span>
                            <span>•</span>
                            <Badge variant="outline" className="text-xs">
                              {ticket.source}
                            </Badge>
                          </div>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(ticket.status)}`}>
                          {ticket.status}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <CheckCircle className="h-12 w-12 mx-auto mb-2 opacity-30 text-green-500" />
                    <p>No issues raised</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Segments & CRM */}
          <div className="space-y-6">
            {/* Customer Segments */}
            <Card data-testid="segments-section">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Tags className="h-5 w-5" />
                  Customer Segments
                </CardTitle>
              </CardHeader>
              <CardContent>
                {segments.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {segments.map((segment) => (
                      <Badge 
                        key={segment.segment_id}
                        variant="secondary"
                        className="px-3 py-1"
                        style={segment.color ? { backgroundColor: segment.color, color: '#fff' } : {}}
                      >
                        {segment.name}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4 text-gray-500">
                    <Users className="h-8 w-8 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">Not in any segment</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* CRM Links */}
            <Card data-testid="crm-section">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5" />
                  CRM & Communication
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {crm_links.map((crm) => (
                  <div 
                    key={crm.platform}
                    className="flex items-center justify-between p-3 border rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      {crm.platform === 'whatsapp' ? (
                        <div className="h-10 w-10 bg-green-500 rounded-full flex items-center justify-center">
                          <svg viewBox="0 0 24 24" className="h-6 w-6 text-white" fill="currentColor">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                            <path d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.789l4.94-1.563A11.927 11.927 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-2.313 0-4.484-.646-6.33-1.767l-.453-.277-3.013.953.906-2.892-.3-.478A9.953 9.953 0 012 12c0-5.514 4.486-10 10-10s10 4.486 10 10-4.486 10-10 10z"/>
                          </svg>
                        </div>
                      ) : (
                        <div className="h-10 w-10 bg-blue-600 rounded-full flex items-center justify-center">
                          <span className="text-white font-bold text-sm">Z</span>
                        </div>
                      )}
                      <div>
                        <p className="font-medium capitalize">{crm.platform === 'whatsapp' ? 'WhatsApp' : 'Zoho CRM'}</p>
                        <p className="text-xs text-gray-500">
                          {crm.connected ? (crm.url ? 'Connected' : 'Connected (no contact)') : 'Not connected'}
                        </p>
                      </div>
                    </div>
                    {crm.url ? (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => window.open(crm.url, '_blank')}
                      >
                        <ExternalLink className="h-4 w-4 mr-1" />
                        Open
                      </Button>
                    ) : (
                      <Badge variant={crm.connected ? 'secondary' : 'outline'}>
                        {crm.connected ? 'No Contact' : 'Not Setup'}
                      </Badge>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Customer Info */}
            <Card data-testid="customer-info-card">
              <CardHeader>
                <CardTitle>Customer Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-sm text-gray-600">Customer ID</p>
                  <p className="font-mono text-sm">{customer.id}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Account Created</p>
                  <p className="font-medium">{formatDate(customer.created_at)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Last Updated</p>
                  <p className="font-medium">{formatDate(customer.updated_at)}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Edit Customer Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Customer</DialogTitle>
            <DialogDescription>
              Update customer information
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Name</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                data-testid="edit-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={formData.email}
                disabled
              />
              <p className="text-xs text-gray-500">Email cannot be changed</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} data-testid="save-customer-btn">
              <Save className="h-4 w-4 mr-2" />
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default CustomerDetailsNew;
